import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, CheckCircle, XCircle, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ProfileStatus {
  status: 'draft' | 'pending_verification' | 'verified' | 'rejected';
  completionPercentage: number;
  currentStep?: number;
  verificationSubmittedAt?: string;
  rejectionReason?: string;
}

export function ProfileStatusBanner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ProfileStatus | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadProfileStatus();
    // Check if verified banner was dismissed
    const dismissed = localStorage.getItem('verified_banner_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  const loadProfileStatus = async () => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://npm.rapidfix.ie';
      const response = await fetch(
        `${apiBaseUrl}/api/v1/auth/providers/profile`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      
      console.log('ProfileStatusBanner - API Base URL:', apiBaseUrl);
      console.log('ProfileStatusBanner - Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('ProfileStatusBanner - Profile data:', data);
        
        setStatus({
          status: data.status || 'draft',
          completionPercentage: Math.round((data.completed_steps?.length || 0) * 100 / 5),
          currentStep: data.current_step || 1,
          verificationSubmittedAt: data.verification_submitted_at,
          rejectionReason: data.rejection_reason
        });
      } else {
        console.error('ProfileStatusBanner - API error:', response.status);
        // If API fails or returns error, default to draft status
        setStatus({
          status: 'draft',
          completionPercentage: 0,
          currentStep: 1
        });
      }
    } catch (err) {
      console.error('Failed to load profile status:', err);
      // On error, default to draft status so banner still shows
      setStatus({
        status: 'draft',
        completionPercentage: 0,
        currentStep: 1
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('verified_banner_dismissed', 'true');
  };

  if (isLoading || !status) return null;
  if (status.status === 'verified' && isDismissed) return null;

  // Draft/Incomplete - Compact banner
  if (status.status === 'draft' && status.completionPercentage < 20) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg mb-4 bg-red-50 border border-red-200">
        <div className="flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-900">
            Complete your profile to receive jobs • {status.completionPercentage}% done
          </p>
        </div>
        <Button 
          size="sm"
          onClick={() => navigate('/profile/provider')}
          className="bg-red-600 hover:bg-red-700 whitespace-nowrap"
        >
          Complete Now
        </Button>
      </div>
    );
  }

  // Pending Verification - Compact banner
  if (status.status === 'pending_verification') {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg mb-4 bg-amber-50 border border-amber-200">
        <div className="flex-shrink-0">
          <Clock className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-900">
            Profile under review • Typically takes 24-48 hours
          </p>
        </div>
        <Button 
          size="sm"
          variant="outline"
          onClick={() => navigate('/profile/status')}
          className="border-amber-600 text-amber-700 hover:bg-amber-100 whitespace-nowrap"
        >
          View Status
        </Button>
      </div>
    );
  }

  // Rejected - Compact banner
  if (status.status === 'rejected') {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg mb-4 bg-red-50 border border-red-200">
        <div className="flex-shrink-0">
          <XCircle className="w-5 h-5 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-900">
            Profile needs updates • Review admin feedback
          </p>
        </div>
        <Button 
          size="sm"
          onClick={() => navigate('/profile/provider')}
          className="bg-red-600 hover:bg-red-700 whitespace-nowrap"
        >
          Update Now
        </Button>
      </div>
    );
  }

  // Verified - Compact dismissible banner
  if (status.status === 'verified') {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-lg mb-4 bg-emerald-50 border border-emerald-200">
        <div className="flex-shrink-0">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-emerald-900">
            ✅ You're verified! Customers can now find and hire you
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-black/5 rounded transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>
      </div>
    );
  }

  return null;
}
